/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useCallback, useEffect, useState} from "react";
import styled from "styled-components";
import VariablesTable from "./VariablesTable";
import Loader from "../../../components/Loader/Loader";
import {usePostRequest} from "../../../../api/helper";
import {SCENARIO_ANALYSIS_CONSTANTS} from "../constants";
import toast from "react-hot-toast";
import ModelNotSelected from "./ModelNotSelected";


interface ConfigureProps {
  model: string
  version: string
  variation: string
}


const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  width: '100%',
  '> *': {
    display: 'flex',
    justifyContent: 'center'
  }
})


/**
 * Renders the Configure tab content on the View Model page
 */
const Configure = (props: ConfigureProps) => {
  const {model, version, variation} = props

  const [variablesTableData, setVariablesTableData] = useState<any | null>([])
  const [isFetchModelVariablesTableLoading, setIsFetchModelVariablesTableLoading] = useState<boolean>(false)

  /**
   * Fetch model variables table content
   */
  const modelVariablesTableUrl = SCENARIO_ANALYSIS_CONSTANTS.viewModel.endpoints.modelParameterList
  const fetchModelVariablesTableHandle = usePostRequest(modelVariablesTableUrl)
  const fetchModelVariablesTable = useCallback(async () => {
    if (!model || !version || !variation) return
    setIsFetchModelVariablesTableLoading(true)
    const body = {model, version, variation, variationIsId: true}
    fetchModelVariablesTableHandle(body).then((data) => {
      setVariablesTableData(data.table_data)
    }).catch((e) => {
      console.log(e)
      toast.error("An error occurred while fetching the Configure content")
    }).then(() => {
      setIsFetchModelVariablesTableLoading(false)
    })
  }, [model, version, variation])


  /**
   * Fetch and set the model's Configure tab when a different model, version, and 
   * variation is selected
   */
  useEffect(() => {
    if (!model || !version || !variation) return
    fetchModelVariablesTable()
  }, [model, version, variation])

  return (
    <Container>
      {(!model || !version || !variation) ? 
        <ModelNotSelected/>
      : <Loader isLoading={isFetchModelVariablesTableLoading}>
        <VariablesTable tableData={variablesTableData} model={model} version={version} variation={variation}/>
      </Loader>}
    </Container>
  )
}

export default Configure
